import React from "react";
import styled from "@emotion/styled";
import {
  Paper as MuiPaper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { spacing } from "@mui/system";
import { tableCellClasses } from "@mui/material/TableCell";

const Paper = styled(MuiPaper)(spacing);

const TableWrapper = styled.div`
  overflow-y: auto;
  max-width: calc(100vw - ${(props) => props.theme.spacing(12)});
`;

const CustomTableCell = styled(TableCell)`
  &.${tableCellClasses.head} {
    background: ${(props) => props.theme.palette.primary.main};
    color: ${(props) => props.theme.palette.common.white};
  }
  &.${tableCellClasses.body} {
    font-size: 14px;
  }
`;

const CustomTableRow = styled(TableRow)`
  &:nth-of-type(odd) {
    background-color: rgba(0, 0, 0, 0.025);
  }
`;

/**
 * BasicTable - A simple table component without card wrapper
 *
 * @param {Object} props
 * @param {Array} props.columns - Array of column definitions: [{id: 'name', label: 'Name', align: 'left'}]
 * @param {Array} props.data - Array of data objects
 * @param {boolean} props.customized - Use custom styled header and rows
 * @param {boolean} props.showTableHeader - Show the table header row with column names
 * @param {Function} props.renderCellContent - Function to render cell content (row, column, index)
 * @param {Object} props.paperProps - Additional props for the Paper component
 * @param {string} props.height - Fixed height for the table
 * @param {Function} props.onRowClick - Function called when a row is clicked (row, index)
 */
const BasicTable = ({
  columns = [],
  data = [],
  customized = false,
  showTableHeader = true,
  renderCellContent,
  paperProps = {},
  height,
  onRowClick,
}) => {
  const TableComponent = customized ? CustomTableRow : TableRow;
  const CellComponent = customized ? CustomTableCell : TableCell;

  // Default table cell renderer
  const defaultRenderCellContent = (row, column, index) => {
    return row[column.id];
  };

  // Use custom renderer or the default one
  const cellRenderer = renderCellContent || defaultRenderCellContent;

  // Handle row click
  const handleRowClick = (row, index) => {
    if (onRowClick) {
      onRowClick(row, index);
    }
  };

  return (
    <Paper {...paperProps}>
      <TableWrapper style={height ? { height, overflowY: "auto" } : {}}>
        <Table>
          {showTableHeader && (
            <TableHead>
              <TableRow>
                {columns.map((column, index) => (
                  <CellComponent
                    key={column.id || index}
                    align={column.align || "left"}
                    style={column.style || {}}
                    width={column.width}
                  >
                    {column.label}
                  </CellComponent>
                ))}
              </TableRow>
            </TableHead>
          )}
          <TableBody>
            {data.length > 0 ? (
              data.map((row, rowIndex) => (
                <TableComponent
                  key={row.id || rowIndex}
                  hover={!customized}
                  onClick={() => handleRowClick(row, rowIndex)}
                  style={row.style || {}}
                >
                  {columns.map((column, colIndex) => (
                    <CellComponent
                      key={column.id || colIndex}
                      component={colIndex === 0 ? "th" : "td"}
                      scope={colIndex === 0 ? "row" : null}
                      align={column.align || "left"}
                      style={column.cellStyle || {}}
                    >
                      {cellRenderer(row, column, colIndex)}
                    </CellComponent>
                  ))}
                </TableComponent>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  align="center"
                  style={{ padding: "24px" }}
                >
                  <Typography variant="body1" color="textSecondary">
                    No data available
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableWrapper>
    </Paper>
  );
};

export default BasicTable;
